<script setup lang="ts">
import { computed, ref } from "vue";
import type { AdminExpertDetail, PlanInvitation } from "@shopexperts/legacy-api";
import {
  UiAside,
  UiButton,
  UiCard,
  UiChip,
  UiDialog,
  UiIcon,
  UiIconButton,
  UiInfoBox,
  UiText,
  UiTooltip,
  useToaster,
} from "@shopexperts/shared";
import { useAdminExpertPlans } from "@/app/composables/expert-plans/useAdminExpertPlans";
import { useAdminPlanInvitations } from "@/app/composables/expert-billing/useAdminPlanInvitations";
import { useCreatePlanInvitation } from "@/app/composables/expert-billing/useCreatePlanInvitation";
import { useCancelPlanInvitation } from "@/app/composables/expert-billing/useCancelPlanInvitation";
import {
  formatInvitationActor,
  formatInvitationDate,
  formValuesToCreateInvitationPayload,
  invitationLifecycleText,
  invitationStatusDisplay,
  isInvitationFeatureUnavailable,
  planMatchesExpertType,
  type PaymentInvitationFormValues,
} from "@/app/composables/expert-billing/paymentInvitation";
import { formatBillingInterval } from "@/app/composables/expert-billing/billingOptionForm";
import PaymentInvitationForm from "./PaymentInvitationForm.vue";

const props = defineProps<{
  expert: AdminExpertDetail;
}>();

const toaster = useToaster();
const expertId = computed(() => props.expert.id);
const isIssueAsideOpen = ref(false);
const isFeatureUnavailable = ref(false);
const invitationPendingCancel = ref<PlanInvitation | null>(null);
const isCancelDialogOpen = computed({
  get: () => invitationPendingCancel.value !== null,
  set: (open) => {
    if (!open) invitationPendingCancel.value = null;
  },
});

const { invitations, isLoading, error, refetch } = useAdminPlanInvitations(expertId);
const { plans, isLoading: isLoadingPlans } = useAdminExpertPlans({ status: "active" });
const createInvitation = useCreatePlanInvitation();
const cancelInvitation = useCancelPlanInvitation();

const eligiblePlans = computed(() => plans.value.filter((plan) => (
  planMatchesExpertType(plan, props.expert.expert_type)
)));

function openIssueAside() {
  isIssueAsideOpen.value = true;
}

async function issueInvitation(values: PaymentInvitationFormValues) {
  try {
    await createInvitation.mutateAsync({
      expertId: props.expert.id,
      data: formValuesToCreateInvitationPayload(values),
    });
    isIssueAsideOpen.value = false;
  } catch (issueError) {
    if (isInvitationFeatureUnavailable(issueError)) {
      isFeatureUnavailable.value = true;
      isIssueAsideOpen.value = false;
    }
  }
}

function promptCancel(invitation: PlanInvitation) {
  invitationPendingCancel.value = invitation;
}

async function confirmCancel() {
  if (!invitationPendingCancel.value) return;
  try {
    await cancelInvitation.mutateAsync({
      expertId: props.expert.id,
      invitationId: invitationPendingCancel.value.id,
    });
    invitationPendingCancel.value = null;
  } catch {
    // The mutation presents the API response and keeps the dialog available for retry.
  }
}

async function copyPaymentUrl(invitation: PlanInvitation) {
  if (!invitation.payment_url) return;
  try {
    await navigator.clipboard.writeText(invitation.payment_url);
    toaster.create({ title: "Payment link copied", type: "success" });
  } catch {
    toaster.create({ title: "Could not copy the payment link", type: "error" });
  }
}

function invitationOffer(invitation: PlanInvitation): string {
  const plan = invitation.plan?.name ?? `Plan #${invitation.v2_plan_id}`;
  const option = invitation.billing_option?.label ?? `Option #${invitation.v2_plan_billing_option_id}`;
  return `${plan} - ${option}`;
}

function invitationOfferMeta(invitation: PlanInvitation): string | null {
  if (!invitation.billing_option) return null;
  const amount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: invitation.billing_option.currency,
  }).format(invitation.billing_option.amount_cents / 100);
  return `${amount} · ${formatBillingInterval(invitation.billing_option)}`;
}
</script>

<template>
  <UiCard padding="md">
    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="min-w-0 space-y-1">
          <div class="flex flex-wrap items-center gap-2">
            <UiText as="h3" variant="h4" class="font-semibold text-se-dark" text="Payment invitations" />
            <UiChip
              v-if="invitations.length"
              :text="String(invitations.length)"
              variant="neutral"
              size="xs"
              rounded="full"
            />
          </div>
          <UiText
            as="p"
            variant="p-small"
            class="max-w-2xl text-se-gray-700"
            text="Send this expert a secure link to start a Stripe subscription, and track every invitation through its full lifecycle."
          />
        </div>
        <UiButton
          variant="primary"
          size="md"
          icon="arrow-send"
          label="Issue invitation"
          :disabled="isLoadingPlans || isFeatureUnavailable"
          class="shrink-0"
          @click="openIssueAside"
        />
      </div>

      <UiInfoBox
        v-if="isFeatureUnavailable"
        variant="warning"
        title="Issuing invitations is not enabled"
      >
        <UiText as="p" variant="p-small" class="text-se-gray-700">
          The API feature flag is off. Existing invitations can still be viewed, copied, and cancelled here.
        </UiText>
      </UiInfoBox>

      <div v-if="isLoading" class="flex items-center justify-center gap-3 py-12">
        <span class="h-5 w-5 animate-spin rounded-full border-2 border-se-gray-300 border-t-se-dark" />
        <UiText as="span" variant="p-small" class="text-se-gray-700" text="Loading payment invitations…" />
      </div>

      <div v-else-if="error" class="flex flex-col items-center gap-3 py-10 text-center">
        <UiIcon name="info-circle" class="h-8 w-8 text-se-gray-500" />
        <UiText as="p" variant="p-small" class="text-se-gray-700" text="Payment invitations could not be loaded." />
        <UiButton variant="outline" label="Try again" @click="refetch()" />
      </div>

      <div v-else-if="invitations.length === 0" class="rounded-lg bg-se-gray-100 px-6 py-10 text-center">
        <div class="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-se-white">
          <UiIcon name="link-external" class="h-5 w-5 text-se-gray-600" />
        </div>
        <UiText as="p" variant="p" class="font-semibold text-se-dark" text="No payment invitations yet" />
        <UiText
          as="p"
          variant="p-small"
          class="mx-auto mt-1 max-w-md text-se-gray-700"
          text="Issue an invitation when this expert is ready to choose a Stripe-backed subscription offer."
        />
      </div>

      <div v-else class="overflow-x-auto border-t border-se-gray-200">
        <table class="w-full table-auto border-collapse text-left">
          <thead>
            <tr class="border-b border-se-gray-200">
              <th class="py-3 pr-6 font-normal">
                <UiText as="span" variant="caption" class="text-se-gray-600" text="OFFER" />
              </th>
              <th class="px-4 py-3 font-normal">
                <UiText as="span" variant="caption" class="text-se-gray-600" text="STATUS" />
              </th>
              <th class="px-4 py-3 font-normal">
                <UiText as="span" variant="caption" class="text-se-gray-600" text="ISSUED" />
              </th>
              <th class="px-4 py-3 font-normal">
                <UiText as="span" variant="caption" class="text-se-gray-600" text="LIFECYCLE" />
              </th>
              <th class="py-3 pl-4 text-right font-normal">
                <UiText as="span" variant="caption" class="text-se-gray-600" text="ACTIONS" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="invitation in invitations"
              :key="invitation.id"
              class="border-b border-se-gray-200 last:border-b-0"
            >
              <td class="min-w-56 py-4 pr-6 align-top">
                <UiText as="p" variant="p-small" class="font-semibold text-se-dark">
                  {{ invitationOffer(invitation) }}
                </UiText>
                <div class="mt-1 flex flex-wrap items-center gap-2">
                  <UiText
                    v-if="invitationOfferMeta(invitation)"
                    as="span"
                    variant="caption-normal"
                    class="text-se-gray-600"
                  >{{ invitationOfferMeta(invitation) }}</UiText>
                  <UiChip
                    :text="invitation.mode === 'live' ? 'Live' : 'Test'"
                    :variant="invitation.mode === 'live' ? 'success' : 'outline'"
                    size="xs"
                    rounded="small"
                    :tooltip="invitation.mode === 'live' ? 'Uses the live Stripe account' : 'Uses Stripe test mode'"
                  />
                </div>
              </td>
              <td class="whitespace-nowrap px-4 py-4 align-top">
                <UiChip
                  :text="invitationStatusDisplay(invitation.status).label"
                  :variant="invitationStatusDisplay(invitation.status).variant"
                  :bg-color-class="invitationStatusDisplay(invitation.status).bgColorClass"
                  :text-color-class="invitationStatusDisplay(invitation.status).textColorClass"
                  size="xs"
                  rounded="full"
                />
              </td>
              <td class="min-w-44 px-4 py-4 align-top">
                <UiText as="p" variant="p-small" class="text-se-dark">
                  {{ formatInvitationActor(invitation.created_by) }}
                </UiText>
                <UiText as="p" variant="caption-normal" class="mt-0.5 text-se-gray-600">
                  {{ formatInvitationDate(invitation.created_at) }}
                </UiText>
              </td>
              <td class="min-w-52 px-4 py-4 align-top">
                <UiText as="p" variant="p-small" class="text-se-gray-700">
                  {{ invitationLifecycleText(invitation) }}
                </UiText>
                <UiText
                  v-if="invitation.status === 'superseded' && invitation.superseded_by_invitation_id"
                  as="p"
                  variant="caption-normal"
                  class="mt-0.5 text-se-gray-600"
                >Invitation #{{ invitation.id }}</UiText>
              </td>
              <td class="py-4 pl-4 align-top">
                <div class="flex min-w-32 items-center justify-end gap-2">
                  <UiTooltip v-if="invitation.payment_url" content="Open payment link">
                    <UiIconButton
                      icon="link-external"
                      variant="outline"
                      :href="invitation.payment_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="h-10 w-10"
                      :aria-label="`Open invitation #${invitation.id}`"
                    />
                  </UiTooltip>
                  <UiTooltip v-if="invitation.payment_url" content="Copy payment link">
                    <UiIconButton
                      icon="copy"
                      variant="outline"
                      class="h-10 w-10"
                      :aria-label="`Copy invitation #${invitation.id}`"
                      @click="copyPaymentUrl(invitation)"
                    />
                  </UiTooltip>
                  <UiTooltip v-if="invitation.status === 'pending'" content="Cancel invitation">
                    <UiIconButton
                      icon="close"
                      variant="danger"
                      class="h-10 w-10"
                      :aria-label="`Cancel invitation #${invitation.id}`"
                      :disabled="cancelInvitation.isPending.value"
                      @click="promptCancel(invitation)"
                    />
                  </UiTooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </UiCard>

  <UiAside v-model="isIssueAsideOpen" position="right" :close-on-overlay-click="false">
    <PaymentInvitationForm
      v-if="isIssueAsideOpen"
      :plans="eligiblePlans"
      :is-submitting="createInvitation.isPending.value"
      @submit="issueInvitation"
      @cancel="isIssueAsideOpen = false"
    />
  </UiAside>

  <UiDialog
    v-model="isCancelDialogOpen"
    v-model:loading="cancelInvitation.isPending.value"
    title="Cancel payment invitation"
    primary-action-label="Cancel invitation"
    primary-action-variant="danger"
    secondary-action-label="Keep invitation"
    size="sm"
    @primary-action="confirmCancel"
    @secondary-action="invitationPendingCancel = null"
  >
    <div class="space-y-3">
      <UiText as="p" variant="p-small" class="text-se-gray-700">
        Cancel invitation #{{ invitationPendingCancel?.id }} for
        <span class="font-semibold text-se-dark">{{ invitationPendingCancel ? invitationOffer(invitationPendingCancel) : 'this offer' }}</span>?
      </UiText>
      <UiInfoBox variant="warning" title="The payment link will stop working">
        <UiText as="p" variant="p-small" class="text-se-gray-700">
          Cancellation is safe to retry. If the expert has an active Stripe Checkout, the invitation stays locked until that Checkout expires.
        </UiText>
      </UiInfoBox>
    </div>
  </UiDialog>
</template>
